import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import Crown from "@/components/Crown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "na_exit_intent_seen";    // shown this tab session?
const DISMISS_KEY = "na_exit_intent_dismissed_at"; // last hard-dismiss timestamp
const SUPPRESS_DAYS = 7;
const MOBILE_DELAY_MS = 25_000;
const MOBILE_SCROLL_PCT = 0.6;

const isMobile = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;

const isSuppressed = () => {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return true;
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageMs = Date.now() - Number(ts);
    return ageMs < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const ExitIntentPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const armedRef = useRef(false);

  // Only arm on home page; never on the freebie page itself.
  const allowedRoute = pathname === "/" || pathname === "";

  // Manual preview via URL hash — lets the owner preview the popup any time
  useEffect(() => {
    const checkHash = () => {
      if (typeof window === "undefined") return;
      if (window.location.hash === "#preview-mirror") {
        armedRef.current = true;
        setOpen(true);
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  useEffect(() => {
    if (!allowedRoute) return;
    if (isSuppressed()) return;

    const trigger = () => {
      if (armedRef.current) return;
      armedRef.current = true;
      setOpen(true);
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
    };

    // Desktop: real exit-intent — mouse leaves through the top edge of the document
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };

    // Mobile fallback: long-enough session + scrolled past threshold
    let mobileTimer;
    const onScroll = () => {
      const h = document.documentElement;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      const pct = h.scrollTop / max;
      if (pct >= MOBILE_SCROLL_PCT) trigger();
    };

    if (isMobile()) {
      mobileTimer = window.setTimeout(() => {
        window.addEventListener("scroll", onScroll, { passive: true });
        // Also fire if they idle this long with enough already scrolled
        onScroll();
      }, MOBILE_DELAY_MS);
    } else {
      document.documentElement.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      if (mobileTimer) window.clearTimeout(mobileTimer);
    };
  }, [allowedRoute]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    // lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/freebie`, {
        first_name: first.trim(),
        email: email.trim(),
      });
      setDone(true);
      toast.success("Your guide is on its way — opening it now.");
      const a = document.createElement("a");
      a.href = `${API}/freebie/download`;
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="exit-pop-backdrop"
      data-testid="exit-intent-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="exit-pop" data-testid="exit-intent-popup" role="dialog" aria-modal="true">
        <button
          className="exit-pop-x"
          aria-label="Close"
          onClick={close}
          data-testid="exit-intent-close"
        >×</button>

        <div className="exit-pop-inner">
          <div className="exit-pop-crown"><Crown size={36} opacity={0.95} /></div>

          {done ? (
            <>
              <div className="exit-pop-eyebrow">DELIVERED</div>
              <h3 className="exit-pop-title">
                It’s yours. <span className="italic-c text-gold">The download just opened.</span>
              </h3>
              <p className="exit-pop-sub">
                Check your inbox at <strong>{email}</strong> for the PDF + the next step.
              </p>
              <button
                className="btn btn-primary"
                onClick={close}
                data-testid="exit-intent-done-close"
                style={{ marginTop: 18 }}
              >
                Got it →
              </button>
            </>
          ) : (
            <>
              <div className="exit-pop-eyebrow">WAIT — BEFORE YOU GO</div>
              <h3 className="exit-pop-title">
                Take <span className="italic-c text-gold">The Mirror Method</span> with you.
              </h3>
              <p className="exit-pop-sub">
                A free guide for high-performing women who are ready to stop performing
                and start telling the truth. Five prompts. One reframe. Yours in 30 seconds.
              </p>

              <form className="exit-pop-form" onSubmit={submit} data-testid="exit-intent-form">
                <input
                  className="input-base"
                  placeholder="First name"
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  required
                  data-testid="exit-intent-first"
                />
                <input
                  className="input-base"
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="exit-intent-email"
                />
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loading}
                  data-testid="exit-intent-submit"
                >
                  {loading ? "Sending…" : "Send Me the Guide →"}
                </button>
              </form>

              <button
                type="button"
                className="exit-pop-decline"
                onClick={close}
                data-testid="exit-intent-decline"
              >
                No thanks, I’ll keep performing.
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
